import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import {
  addFavourite,
  listFavourites,
  removeFavourite,
  updateFavourite,
} from "@/features/favourites/api/favouritesApi";
import type {
  AddFavouritePayload,
  Favourite,
} from "@/features/favourites/types";

const KEY = ["favourites", "mine"];

export function useFavourites() {
  return useQuery<Favourite[]>({ queryKey: KEY, queryFn: listFavourites });
}

export function useAddFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: AddFavouritePayload) => addFavourite(payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useUpdateFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, notes }: { id: string; notes: string | null }) =>
      updateFavourite(id, notes),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

export function useRemoveFavourite() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (workerId: string) => removeFavourite(workerId),
    onSuccess: () => qc.invalidateQueries({ queryKey: KEY }),
  });
}

/** Quick helper for components that want to know if a worker is already a fav. */
export function useIsFavourite(workerId: string | undefined): boolean {
  const { data } = useFavourites();
  if (!workerId || !data) return false;
  return data.some((f) => f.worker_id === workerId);
}
